import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { TextMetrics } from "pixi.js";
import { Settings } from "../../../../../../settings";
import { PixiSettings } from "../../../../../../pixiSettings";

// Static mode configuration - Desktop
export const STATIC_CONFIG_DESKTOP = {
  widthMultiplier: 0.5, // Controls size (height scales proportionally)
  horizontalOffset: 0, // In symbol sizes (0 = centered)
  verticalOffset: 0.25, // In symbol sizes (0 = centered)
  spacing: -1.8, // Gap between sprites in symbol sizes (0 = touching at center)
  layout: "horizontal", // horizontal = side by side
};

// Static mode configuration - Tablet Landscape
export const STATIC_CONFIG_TABLET_LANDSCAPE = {
  widthMultiplier: 0.6,
  horizontalOffset: 0,
  verticalOffset: 0.25,
  spacing: -2.5,
  layout: "horizontal",
};

// Static mode configuration - Tablet Portrait
export const STATIC_CONFIG_TABLET_PORTRAIT = {
  widthMultiplier: 0.45,
  horizontalOffset: 0,
  verticalOffset: 0.5,
  spacing: -2.0,
  layout: "vertical", // vertical = stacked
};

// Static mode configuration - Mobile Landscape
export const STATIC_CONFIG_MOBILE_LANDSCAPE = {
  widthMultiplier: 0.39,
  horizontalOffset: 0,
  verticalOffset: 0.25,
  spacing: -1.75,
  layout: "horizontal",
};

// Static mode configuration - Mobile Portrait
export const STATIC_CONFIG_MOBILE_PORTRAIT = {
  widthMultiplier: 0.4,
  horizontalOffset: 0,
  verticalOffset: 0.5,
  spacing: -1.75,
  layout: "vertical",
};

/**
 * Get static config based on device and orientation
 */
export const getStaticConfig = (device, orientation) => {
  if (device === PixiSettings.DEVICE.MOBILE) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return STATIC_CONFIG_MOBILE_PORTRAIT;
    } else {
      return STATIC_CONFIG_MOBILE_LANDSCAPE;
    }
  } else if (device === PixiSettings.DEVICE.TABLET) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return STATIC_CONFIG_TABLET_PORTRAIT;
    } else {
      return STATIC_CONFIG_TABLET_LANDSCAPE;
    }
  } else {
    // Desktop
    return STATIC_CONFIG_DESKTOP;
  }
};

/**
 * IntroPopupController - Handles intro popup spine animation
 */
class IntroPopupController {
  constructor(container, dimensions, uiSymbolSize, dimensionsContext, t) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = uiSymbolSize;
    this.symbolHeight = uiSymbolSize;
    this.dimensionsContext = dimensionsContext;
    this.t = t; // Translation function

    // Spine instance
    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;

    // Text containers attached to spine bones
    this.maxWinContainer = null;
    this.maxWinText = null;
    this.tapToStartContainer = null;
    this.tapToStartText = null;
    this.basegameContainer = null;
    this.basegameText = null;
    this.hnsContainer = null;
    this.hnsText = null;

    // Bone update ticker
    this.boneUpdateTicker = null;

    this.initializeIntroSpine();
  }

  /**
   * Proportional scaling based on symbol size with device-specific multipliers
   */
  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions || !this.initialized) return;

    // Additional safety check - ensure spine is still valid
    if (!this.introSpine.scale || !this.introSpine.parent) return;

    // Use GRID_SYMBOL_HEIGHT since uiSymbolSize is based on GRID_SYMBOL_HEIGHT, not SYMBOL_HEIGHT
    const nativeSize = Settings.GRID_SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Get device/orientation-specific multipliers
    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    try {
      this.introSpine.scale.set(scale);

      // Apply device/orientation-specific offset multiplier
      const baseOffset = this.symbolHeight || 0;

      this.introSpine.x = this.dimensions.width / 2;
      this.introSpine.y =
        this.dimensions.height / 2 - baseOffset * deviceOffsetMultiplier;
    } catch (error) {
      console.log(
        "IntroPopupController: Failed to apply scale and center",
        error
      );
    }
  }

  /**
   * Get device-specific multipliers for scale and offset
   * Returns an object with scale and offset multipliers
   */
  getDeviceMultipliers() {
    if (!this.dimensionsContext) {
      console.log("IntroPopupController: No dimensionsContext available");
      return { scale: 1.0, offset: 1.0 };
    }

    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // Mobile portrait: smaller size to fit better
        return { scale: 1.0, offset: -0.5 };
      } else {
        // Mobile landscape: slightly smaller
        return { scale: 0.9, offset: -0.1 };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // Tablet portrait: slightly smaller
        return { scale: 1.0, offset: -0.5 };
      } else {
        // Tablet landscape: normal size
        return { scale: 1.0, offset: 0.0 };
      }
    } else {
      // Desktop: full size
      return { scale: 0.85, offset: -0.2 };
    }
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * Also checks for device/orientation changes and switches animation if needed
   */
  updateDimensions(dimensions, uiSymbolSize, dimensionsContext) {
    // Don't update if spine has been cleaned up
    if (!this.initialized || !this.introSpine) return;

    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;

    const symbolWidthChanged = this.symbolWidth !== uiSymbolSize;
    const symbolHeightChanged = this.symbolHeight !== uiSymbolSize;

    // Check if device/orientation changed
    const deviceChanged =
      !this.dimensionsContext ||
      this.dimensionsContext.device !== dimensionsContext?.device ||
      this.dimensionsContext.orientation !== dimensionsContext?.orientation;

    if (
      !dimensionsChanged &&
      !symbolWidthChanged &&
      !symbolHeightChanged &&
      !deviceChanged
    )
      return;

    // Store previous animation name to detect changes
    const previousAnimationName = this.getAnimationName();

    this.dimensions = dimensions;
    this.symbolWidth = uiSymbolSize;
    this.symbolHeight = uiSymbolSize;
    if (dimensionsContext) {
      this.dimensionsContext = dimensionsContext;
    }

    if (
      this.introSpine &&
      this.initialized &&
      (dimensionsChanged || symbolWidthChanged || symbolHeightChanged)
    ) {
      this.applyScaleAndCenter();
    }

    // If device/orientation changed, switch animation instantly
    if (deviceChanged && this.introSpine && this.initialized) {
      const newAnimationName = this.getAnimationName();
      if (previousAnimationName !== newAnimationName) {
        this.playAnimation(true); // Force re-play with new animation
      }
    }
  }

  /**
   * Initialize the intro spine
   */
  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();

      if (!this.introSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.introSpine.skeleton) missingComponents.push("skeleton");
      if (!this.introSpine.state) missingComponents.push("state");
      if (!this.introSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.introSpine);
      this.introSpine.alpha = 1;
      this.introSpine.zIndex = 1;

      // Set initialized before applying scale so applyScaleAndCenter can run
      this.initialized = true;

      // Apply scale and center positioning - this needs initialized to be true
      this.applyScaleAndCenter();

      if (this.introSpine.parent !== this.container) {
        return;
      }

      // Initialize text elements attached to bones
      this.initializeMaxWinText();
      this.initializeTapToStartText();
      this.initializeBasegameText();
      this.initializeHnsText();

      // Start bone update loop
      this.startBoneUpdateLoop();

      this.playAnimation();
    } catch (error) {
      console.log("IntroPopupController: Spine initialization failed", error);
    }
  }

  /**
   * Create intro spine from loaded assets
   */
  createIntroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "intro_screen-skeleton";
      const atlasAlias = "intro_screen-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const introSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!introSpine?.skeleton?.data?.animations) return null;

      return introSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animations from the spine
   */
  getAvailableAnimations() {
    return this.introSpine?.skeleton?.data?.animations.map((a) => a.name) || [];
  }

  /**
   * Get the correct animation name based on device/orientation
   * Mobile portrait -> intro_portrait, everything else -> intro_landscape
   */
  getAnimationName() {
    if (!this.dimensionsContext) {
      return "intro_landscape";
    }

    const { device, orientation } = this.dimensionsContext;

    // Mobile portrait uses intro_portrait, everything else uses intro_landscape
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
    ) {
      return "intro_portrait";
    }

    // Portrait mode on any device should use portrait animation
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return "intro_portrait";
    }

    return "intro_landscape";
  }

  /**
   * Start playing the intro animation based on device/orientation
   */
  playAnimation(force = false) {
    if ((this.isPlaying && !force) || !this.introSpine?.state) return;

    try {
      this.isPlaying = true;
      this.introSpine.alpha = 1;
      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      const animations = this.getAvailableAnimations();
      const targetAnimationName = this.getAnimationName();

      const targetAnimation = animations.find(
        (anim) => anim === targetAnimationName
      );

      if (targetAnimation) {
        // Play the appropriate animation in a loop
        this.introSpine.state.setAnimation(0, targetAnimation, true);
      } else {
        // Fallback to first available animation
        const firstAnim = animations[0];
        if (firstAnim) {
          this.introSpine.state.setAnimation(0, firstAnim, true);
        }
      }
    } catch (error) {
      this.isPlaying = false;
    }
  }

  /**
   * Stop the animation and hide the spine
   */
  stopAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.introSpine.alpha = 0.001;
    if (this.introSpine.state) {
      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
    }
  }

  /**
   * Initialize max win text and attach it to spine's "txt_max_win" bone
   */
  initializeMaxWinText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const maxWinBone = this.introSpine.skeleton.findBone("txt_max_win");
    if (!maxWinBone) {
      console.log("[IntroPopup] txt_max_win bone not found");
      return;
    }

    // Create container as child of spine
    this.maxWinContainer = new PIXI.Container();
    this.maxWinContainer.name = "max_win_text_attached_to_bone";
    this.maxWinContainer.alpha = 1;
    this.maxWinContainer.zIndex = 1000;

    // Get translated text with replaced variable
    const MAX_WIN_MULTIPLIER = 5000;
    const maxWinText = this.t("start_page_max_win").replace(
      "{MAX_MULTIPLIER}",
      MAX_WIN_MULTIPLIER.toLocaleString()
    );

    // Define max dimensions - narrower width and max 3 rows
    const maxWidth = 500; // Narrower to prevent expansion
    const idealFontSize = 32;
    const MAX_ROWS = 3;

    let finalFontSize = idealFontSize;

    // Check if text fits within constraints
    const tempStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: idealFontSize,
      fontWeight: "bold",
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxWidth,
    });

    const metrics = TextMetrics.measureText(maxWinText, tempStyle);
    const lineHeight = metrics.lineHeight || idealFontSize * 1.2;
    const numberOfRows = Math.ceil(metrics.height / lineHeight);

    // Only scale down if text exceeds 3 rows
    if (numberOfRows > MAX_ROWS) {
      for (let i = 0; i < 20; i++) {
        const testStyle = new PIXI.TextStyle({
          fontFamily: "Noto Sans, Arial, sans-serif",
          fontSize: finalFontSize,
          fontWeight: "bold",
          align: "center",
          wordWrap: true,
          wordWrapWidth: maxWidth,
        });

        const testMetrics = TextMetrics.measureText(maxWinText, testStyle);
        const testLineHeight = testMetrics.lineHeight || finalFontSize * 1.2;
        const testRows = Math.ceil(testMetrics.height / testLineHeight);

        if (testRows <= MAX_ROWS) {
          break;
        }

        const rowScale = MAX_ROWS / testRows;
        const scale = rowScale * 0.95;
        finalFontSize = Math.round(finalFontSize * scale);

        if (finalFontSize < 12) {
          finalFontSize = 12;
          break;
        }
      }
    }

    // Create text style (yellow with shadow) with calculated font size
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "bold",
      fill: 0xffe462, // Yellow
      dropShadow: true,
      dropShadowColor: 0x4a0900, // Dark red/brown
      dropShadowDistance: 3,
      dropShadowBlur: 4,
      dropShadowAlpha: 1,
      dropShadowAngle: Math.PI / 2,
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxWidth,
    });

    this.maxWinText = new PIXI.Text(maxWinText, textStyle);
    this.maxWinText.anchor.set(0.5);
    this.maxWinText.x = 0;
    this.maxWinText.y = 0;

    this.maxWinContainer.addChild(this.maxWinText);
    this.introSpine.addChild(this.maxWinContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Initialize tap to start text and attach it to spine's "txt_tap_to_start" bone
   */
  initializeTapToStartText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const tapToStartBone =
      this.introSpine.skeleton.findBone("txt_tap_to_start");
    if (!tapToStartBone) {
      console.log("[IntroPopup] txt_tap_to_start bone not found");
      return;
    }

    // Create container as child of spine
    this.tapToStartContainer = new PIXI.Container();
    this.tapToStartContainer.name = "tap_to_start_text_attached_to_bone";
    this.tapToStartContainer.alpha = 1;
    this.tapToStartContainer.zIndex = 1001;

    // Get translated text
    const tapToStartText = this.t("tap_to_start").toUpperCase();

    // Define max dimensions for the text area
    const maxWidth = 800;
    const idealFontSize = 36;

    // Create temp style to measure text
    const tempStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: idealFontSize,
      fontWeight: "600",
      align: "center",
    });

    // Measure text to check if it fits
    const metrics = TextMetrics.measureText(tapToStartText, tempStyle);

    // Calculate final font size - scale down if text is too wide
    let finalFontSize = idealFontSize;
    if (metrics.width > maxWidth) {
      const scale = maxWidth / metrics.width;
      finalFontSize = Math.round(idealFontSize * scale);
    }

    // Create final text style with adjusted font size
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "600",
      fill: 0xffffff, // White
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 4,
      dropShadowBlur: 4,
      dropShadowAlpha: 0.8,
      dropShadowAngle: Math.PI / 2,
      align: "center",
    });

    this.tapToStartText = new PIXI.Text(tapToStartText, textStyle);
    this.tapToStartText.anchor.set(0.5);
    this.tapToStartText.x = Math.round(0);
    this.tapToStartText.y = Math.round(0);

    this.tapToStartContainer.addChild(this.tapToStartText);
    this.introSpine.addChild(this.tapToStartContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Initialize basegame text and attach it to spine's "txt_basegame" bone
   */
  initializeBasegameText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const basegameBone = this.introSpine.skeleton.findBone("txt_basegame");
    if (!basegameBone) {
      console.log("[IntroPopup] txt_basegame bone not found");
      return;
    }

    // Create container as child of spine
    this.basegameContainer = new PIXI.Container();
    this.basegameContainer.name = "basegame_text_attached_to_bone";
    this.basegameContainer.alpha = 1;
    this.basegameContainer.zIndex = 999;

    // Get translated text
    const basegameText = this.t("start_page_1_title");

    // Define max dimensions for the text area - narrower width
    const maxWidth = 600; // Reduced from 800 to make text narrower
    const idealFontSize = 32;
    const MAX_ROWS = 3; // Maximum number of rows allowed

    let finalFontSize = idealFontSize;

    // Iteratively scale down if text exceeds 3 rows (max 20 iterations)
    for (let i = 0; i < 20; i++) {
      const tempStyle = new PIXI.TextStyle({
        fontFamily: "Noto Sans, Arial, sans-serif",
        fontSize: finalFontSize,
        fontWeight: "bold",
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxWidth,
      });

      const metrics = TextMetrics.measureText(basegameText, tempStyle);
      const lineHeight = metrics.lineHeight || finalFontSize * 1.2;
      const numberOfRows = Math.ceil(metrics.height / lineHeight);

      // Check if text fits within row limit
      if (numberOfRows <= MAX_ROWS) {
        break;
      }

      // Scale down to fit within 3 rows
      const rowScale = MAX_ROWS / numberOfRows;
      const scale = rowScale * 0.95; // Safety margin
      finalFontSize = Math.round(finalFontSize * scale);

      // Prevent font from becoming too small
      if (finalFontSize < 12) {
        finalFontSize = 12;
        break;
      }
    }

    // Create final text style with adjusted font size
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "bold",
      fill: 0xffe462, // Yellow
      dropShadow: true,
      dropShadowColor: 0x4a0900, // Dark red/brown
      dropShadowDistance: 3,
      dropShadowBlur: 4,
      dropShadowAlpha: 1,
      dropShadowAngle: Math.PI / 2,
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxWidth,
    });

    this.basegameText = new PIXI.Text(basegameText, textStyle);
    this.basegameText.anchor.set(0.5);
    this.basegameText.x = Math.round(0);
    this.basegameText.y = Math.round(0);

    this.basegameContainer.addChild(this.basegameText);
    this.introSpine.addChild(this.basegameContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Initialize hold and win text and attach it to spine's "txt_hns" bone
   */
  initializeHnsText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const hnsBone = this.introSpine.skeleton.findBone("txt_hns");
    if (!hnsBone) {
      console.log("[IntroPopup] txt_hns bone not found");
      return;
    }

    // Create container as child of spine
    this.hnsContainer = new PIXI.Container();
    this.hnsContainer.name = "hns_text_attached_to_bone";
    this.hnsContainer.alpha = 1;
    this.hnsContainer.zIndex = 998;

    // Get translated text
    const hnsText = this.t("start_page_2_title");

    // Define max dimensions for the text area - narrower width
    const maxWidth = 600; // Reduced from 800 to make text narrower
    const idealFontSize = 32;
    const MAX_ROWS = 3; // Maximum number of rows allowed

    let finalFontSize = idealFontSize;

    // Iteratively scale down if text exceeds 3 rows (max 20 iterations)
    for (let i = 0; i < 20; i++) {
      const tempStyle = new PIXI.TextStyle({
        fontFamily: "Noto Sans, Arial, sans-serif",
        fontSize: finalFontSize,
        fontWeight: "bold",
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxWidth,
      });

      const metrics = TextMetrics.measureText(hnsText, tempStyle);
      const lineHeight = metrics.lineHeight || finalFontSize * 1.2;
      const numberOfRows = Math.ceil(metrics.height / lineHeight);

      // Check if text fits within row limit
      if (numberOfRows <= MAX_ROWS) {
        break;
      }

      // Scale down to fit within 3 rows
      const rowScale = MAX_ROWS / numberOfRows;
      const scale = rowScale * 0.95; // Safety margin
      finalFontSize = Math.round(finalFontSize * scale);

      // Prevent font from becoming too small
      if (finalFontSize < 12) {
        finalFontSize = 12;
        break;
      }
    }

    // Create final text style with adjusted font size
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "bold",
      fill: 0xffe462, // Yellow
      dropShadow: true,
      dropShadowColor: 0x4a0900, // Dark red/brown
      dropShadowDistance: 3,
      dropShadowBlur: 4,
      dropShadowAlpha: 1,
      dropShadowAngle: Math.PI / 2,
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxWidth,
    });

    this.hnsText = new PIXI.Text(hnsText, textStyle);
    this.hnsText.anchor.set(0.5);
    this.hnsText.x = Math.round(0);
    this.hnsText.y = Math.round(0);

    this.hnsContainer.addChild(this.hnsText);
    this.introSpine.addChild(this.hnsContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Update max win text position from bone
   */
  updateMaxWinFromBone() {
    if (
      !this.introSpine ||
      !this.maxWinContainer ||
      !this.introSpine.skeleton
    ) {
      return;
    }

    const maxWinBone = this.introSpine.skeleton.findBone("txt_max_win");
    if (!maxWinBone) {
      return;
    }

    this.maxWinContainer.x = Math.round(maxWinBone.worldX);
    this.maxWinContainer.y = Math.round(maxWinBone.worldY);
    this.maxWinContainer.rotation =
      (maxWinBone.getWorldRotationX() * Math.PI) / 180;
    this.maxWinContainer.scale.set(
      maxWinBone.getWorldScaleX(),
      maxWinBone.getWorldScaleY()
    );
  }

  /**
   * Update tap to start text position from bone
   */
  updateTapToStartFromBone() {
    if (
      !this.introSpine ||
      !this.tapToStartContainer ||
      !this.introSpine.skeleton
    ) {
      return;
    }

    const tapToStartBone =
      this.introSpine.skeleton.findBone("txt_tap_to_start");
    if (!tapToStartBone) {
      return;
    }

    this.tapToStartContainer.x = Math.round(tapToStartBone.worldX);
    this.tapToStartContainer.y = Math.round(tapToStartBone.worldY);
    this.tapToStartContainer.rotation =
      (tapToStartBone.getWorldRotationX() * Math.PI) / 180;
    this.tapToStartContainer.scale.set(
      tapToStartBone.getWorldScaleX(),
      tapToStartBone.getWorldScaleY()
    );
  }

  /**
   * Update basegame text position from bone
   */
  updateBasegameFromBone() {
    if (
      !this.introSpine ||
      !this.basegameContainer ||
      !this.introSpine.skeleton
    ) {
      return;
    }

    const basegameBone = this.introSpine.skeleton.findBone("txt_basegame");
    if (!basegameBone) {
      return;
    }

    this.basegameContainer.x = Math.round(basegameBone.worldX);
    this.basegameContainer.y = Math.round(basegameBone.worldY);
    this.basegameContainer.rotation =
      (basegameBone.getWorldRotationX() * Math.PI) / 180;
    this.basegameContainer.scale.set(
      basegameBone.getWorldScaleX(),
      basegameBone.getWorldScaleY()
    );
  }

  /**
   * Update hold and win text position from bone
   */
  updateHnsFromBone() {
    if (!this.introSpine || !this.hnsContainer || !this.introSpine.skeleton) {
      return;
    }

    const hnsBone = this.introSpine.skeleton.findBone("txt_hns");
    if (!hnsBone) {
      return;
    }

    this.hnsContainer.x = Math.round(hnsBone.worldX);
    this.hnsContainer.y = Math.round(hnsBone.worldY);
    this.hnsContainer.rotation = (hnsBone.getWorldRotationX() * Math.PI) / 180;
    this.hnsContainer.scale.set(
      hnsBone.getWorldScaleX(),
      hnsBone.getWorldScaleY()
    );
  }

  /**
   * Start syncing text elements with spine bones every frame
   */
  startBoneUpdateLoop() {
    if (!this.introSpine) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.isPlaying) {
          this.updateMaxWinFromBone();
          this.updateTapToStartFromBone();
          this.updateBasegameFromBone();
          this.updateHnsFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing text elements with spine bones
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Cleanup resources - completely destroy spine and reset state
   */
  cleanup() {
    // Stop animation first
    this.isPlaying = false;

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Cleanup text containers - destroy containers with children, no need to destroy text separately
    if (this.maxWinContainer) {
      this.maxWinContainer.destroy({ children: true });
      this.maxWinContainer = null;
      this.maxWinText = null;
    }

    if (this.tapToStartContainer) {
      this.tapToStartContainer.destroy({ children: true });
      this.tapToStartContainer = null;
      this.tapToStartText = null;
    }

    if (this.basegameContainer) {
      this.basegameContainer.destroy({ children: true });
      this.basegameContainer = null;
      this.basegameText = null;
    }

    if (this.hnsContainer) {
      this.hnsContainer.destroy({ children: true });
      this.hnsContainer = null;
      this.hnsText = null;
    }

    if (this.introSpine) {
      try {
        // Clear all animation state
        if (this.introSpine.state) {
          this.introSpine.state.clearListeners();
          this.introSpine.state.clearTracks();
          this.introSpine.state.timeScale = 1.0;
        }

        // Remove from container
        if (this.container?.children.includes(this.introSpine)) {
          this.container.removeChild(this.introSpine);
        }

        // Destroy the spine completely
        this.introSpine.destroy();
        this.introSpine = null;
      } catch (error) {
        // Force set to null even if destroy fails
        this.introSpine = null;
      }
    }

    // Reset all state flags
    this.initialized = false;
    this.isPlaying = false;
  }
}

export default IntroPopupController;
